/**
 * THE PAY STATEMENT CARD.
 *
 * Anthony, 2026-10-03, after David's pay was worked out by hand and drawn
 * as a white card: "I like this card format. I want this ... to be what
 * shows up when you go to pay or payroll in shop desk."
 *
 * So the card is built ONCE, as a list of lines, and drawn twice: as HTML
 * on the screen, and onto a canvas when somebody saves it as an image to
 * text to the tech. Two separate drawings would drift, and the image is
 * the one the tech reads, so it must be the same sum the screen shows.
 *
 * The card is white in both themes on purpose. It is a document, the
 * thing that gets handed over, and it should look like one.
 */

import type { Statement } from "../data/pay.ts";
import { pctLabel } from "../domain/commission.ts";
import { esc, money } from "../ui/dom.ts";

export type CardLine =
  | { k: "row"; label: string; value: string; strong?: boolean; act?: string }
  | { k: "sub"; label: string; value: string; act?: string }
  | { k: "rule" }
  | { k: "head"; label: string };

export type Card = {
  title: string;
  subtitle: string;
  lines: CardLine[];
  totalLabel: string;
  total: string;
  totalTone: "owed" | "square" | "ahead";
  notes: string[];
};

const ROLE: Record<string, string> = {
  mobile_tech: "Mobile tech", shop_tech: "Shop tech", franchise_tech: "Franchise tech",
  advisor: "Service advisor", apprentice: "Apprentice", dispatcher: "Dispatcher", owner: "Owner",
};

export const roleName = (r: string | null | undefined): string => ROLE[r ?? ""] ?? "No plan yet";

/** The deal in one line, the way Anthony says it. */
export function planLine(t: Statement["terms"]): string {
  if (!t || !t.basis) return "No pay plan on file";
  if (t.basis === "hourly") {
    return t.hourly_rate != null ? `Hourly at ${money(t.hourly_rate)}` : "Hourly, rate not set";
  }
  if (t.role === "dispatcher") return "Commission only, dispatch plan";
  const mob = `${pctLabel(Number(t.commission_pct ?? 0))} labor, ${pctLabel(Number(t.parts_pct ?? 0))} markup`;
  const shopL = t.shop_commission_pct ?? t.commission_pct;
  const shopP = t.shop_parts_pct ?? t.parts_pct;
  if (shopL === t.commission_pct && shopP === t.parts_pct) return mob;
  return `Mobile ${mob} · Shop ${pctLabel(Number(shopL ?? 0))} labor, ${pctLabel(Number(shopP ?? 0))} markup`;
}

const day = (iso: string) =>
  new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function buildCard(s: Statement, from: string, to: string, shopName: string): Card {
  const L: CardLine[] = [];
  const first = s.tech.name.split(" ")[0] ?? s.tech.name;

  // Jobs, each with its sum written out underneath.
  for (const j of s.jobs) {
    const what = j.jobNumber
      ? `Job #${j.jobNumber}${j.title ? ` · ${j.title}` : ""}`
      : (j.note ?? "Commission");
    L.push({ k: "row", label: `${what}${j.serviceAt === "shop" ? " (shop)" : ""}`, value: money(j.amount),
      strong: true });
    if (j.labour) {
      L.push({ k: "sub", label: `${pctLabel(j.labourPct)} of ${money(j.labour)} labor`,
        value: money(j.labour * j.labourPct) });
    }
    for (const p of j.parts) {
      const cost = p.cost == null ? null : p.cost * p.quantity;
      L.push({ k: "sub",
        label: `${p.description}: sold ${money(p.sold)}, cost ${cost == null ? "not entered" : money(cost)}`,
        value: cost == null ? "no markup" : `${money(Math.max(p.sold - cost, 0))} markup`,
        act: j.jobId ? `cost:${j.jobId}:${p.id}` : undefined });
    }
    if (j.partsMarkup) {
      L.push({ k: "sub", label: `${pctLabel(j.partsPct)} of ${money(j.partsMarkup)} parts markup`,
        value: money(j.partsMarkup * j.partsPct) });
    }
  }
  if (s.jobs.length) L.push({ k: "rule" });

  if (s.fromHours || s.hours.length) {
    const h = s.hours.reduce((n, x) => n + Number(x.hours), 0);
    L.push({ k: "row", label: `Hours, ${h.toFixed(2)}`, value: money(s.fromHours), strong: true });
    // Say the overtime out loud, so the person can check it against their week.
    if (s.split.ot || s.split.dt) {
      L.push({ k: "row", label: `  of which ${s.split.reg.toFixed(2)} regular, ${s.split.ot.toFixed(2)} at time and a half${
        s.split.dt ? `, ${s.split.dt.toFixed(2)} at double` : ""}`, value: "" });
    }
  }
  for (const b of s.bonuses) {
    L.push({ k: "row", label: `${b.basis === "dispatch" ? "Dispatch" : "Bonus"}: ${b.note ?? ""}`,
      value: `+ ${money(b.amount)}`, act: `bonus:${b.commissionId}` });
  }
  for (const r of s.reimbursements) {
    L.push({ k: "row", label: `Paid back: ${r.reason}`, value: `+ ${money(r.amount)}` });
  }
  for (const c of s.keptCash) {
    L.push({ k: "row", label: `Less: cash ${first} collected${c.reference?.includes("·")
      ? ` (${c.reference.split("·").slice(1).join("·").trim()})` : ""}`, value: `− ${money(c.amount)}` });
  }
  for (const p of s.payouts) {
    L.push({ k: "row", label: `Less: paid ${day(p.paid_at)} by ${p.method === "transfer" ? "Zelle" : p.method}`,
      value: `− ${money(p.amount)}` });
  }

  const tone = s.owed > 0.005 ? "owed" : s.owed < -0.005 ? "ahead" : "square";
  const notes: string[] = [];
  notes.push(`${planLine(s.terms)}.`);
  if (Math.abs(s.owedAllTime - s.owed) > 0.005) {
    notes.push(s.owedAllTime > 0.005 ? `${money(s.owedAllTime)} owed across all time.`
      : s.owedAllTime < -0.005 ? `${money(-s.owedAllTime)} ahead across all time.`
      : "Square across all time.");
  }
  const missing = s.jobs.reduce((n, j) => n + j.parts.filter((p) => p.cost == null).length, 0);
  if (missing) notes.push(`${missing} part${missing === 1 ? "" : "s"} with no cost entered earn no markup until one is.`);

  return {
    title: `${s.tech.name} · Pay Statement`,
    subtitle: `${shopName}  ·  ${day(from)} to ${day(to)}  ·  ${roleName(s.terms?.role)}`,
    lines: L,
    totalLabel: tone === "ahead" ? `${first} is ahead` : tone === "square" ? "All square" : `Owed to ${first}`,
    total: money(Math.abs(s.owed)),
    totalTone: tone,
    notes,
  };
}

export function cardHtml(c: Card, editable: boolean): string {
  const act = (a?: string) => editable && a
    ? `<button class="linkbtn pstate-act" data-act="${esc(a)}">${a.startsWith("cost") ? "real cost" : "remove"}</button>` : "";
  return `<article class="pstate" id="pstate">
    <h2>${esc(c.title)}</h2>
    <p class="pstate-subtitle">${esc(c.subtitle)}</p>
    <div class="pstate-lines">
      ${c.lines.length ? c.lines.map((l) => l.k === "rule" ? `<hr>`
        : l.k === "head" ? `<h3>${esc(l.label)}</h3>`
        : `<div class="pstate-${l.k}${l.k === "row" && l.strong ? " strong" : ""}">
            <span>${esc(l.label)} ${act(l.act)}</span><span>${esc(l.value)}</span></div>`).join("")
        : `<p class="pstate-empty">Nothing earned or paid in this window.</p>`}
    </div>
    <div class="pstate-total t-${c.totalTone}"><span>${esc(c.totalLabel)}</span><b>${esc(c.total)}</b></div>
    ${c.notes.map((n) => `<p class="pstate-note">${esc(n)}</p>`).join("")}
  </article>`;
}

/**
 * The same card, on a canvas, as a PNG. Drawn by hand rather than by a
 * screenshot library: one dependency fewer, and it comes out identical on
 * a phone and a laptop because it never reads the page's layout.
 */
export async function cardPng(c: Card): Promise<Blob> {
  const W = 1200, X = 80, R = W - 80, scale = 1;
  const font = (px: number, bold = false) =>
    `${bold ? "700" : "400"} ${px}px Inter, "Segoe UI", Roboto, Arial, sans-serif`;
  const probe = document.createElement("canvas").getContext("2d");
  if (!probe) throw new Error("This browser cannot draw images.");

  // Wrap a label to the room left beside its value.
  const wrap = (text: string, px: number, bold: boolean, room: number): string[] => {
    probe.font = font(px, bold);
    const out: string[] = [];
    let line = "";
    for (const w of text.split(" ")) {
      const next = line ? `${line} ${w}` : w;
      if (probe.measureText(next).width > room && line) { out.push(line); line = w; } else line = next;
    }
    if (line) out.push(line);
    return out;
  };

  type Op = (g: CanvasRenderingContext2D, y: number) => number;
  const ops: Op[] = [];
  ops.push((g, y) => { g.font = font(52, true); g.fillStyle = "#111"; g.fillText(c.title, X, y + 52); return y + 74; });
  ops.push((g, y) => { g.font = font(26); g.fillStyle = "#666"; g.fillText(c.subtitle, X, y + 26); return y + 52; });
  ops.push((g, y) => { g.fillStyle = "#ddd"; g.fillRect(X, y, R - X, 2); return y + 26; });

  for (const l of c.lines) {
    if (l.k === "rule") {
      ops.push((g, y) => { g.fillStyle = "#eee"; g.fillRect(X, y + 4, R - X, 2); return y + 20; });
      continue;
    }
    if (l.k === "head") continue;
    const sub = l.k === "sub";
    const px = sub ? 26 : 32, bold = l.k === "row" && !!l.strong;
    const x = sub ? X + 30 : X;
    probe.font = font(px, bold);
    const vw = probe.measureText(l.value).width;
    const rows = wrap(l.label, px, bold, R - x - vw - 30);
    ops.push((g, y) => {
      g.font = font(px, bold); g.fillStyle = sub ? "#888" : "#222";
      rows.forEach((t, i) => g.fillText(t, x, y + px + i * (px + 10)));
      g.fillText(l.value, R - vw, y + px);
      return y + rows.length * (px + 10) + (sub ? 8 : 16);
    });
  }

  const tone = c.totalTone === "owed" ? ["#f1f6f1", "#2e7d32", "#1b5e20"]
    : c.totalTone === "ahead" ? ["#fdf5e6", "#b26a00", "#8a5200"] : ["#f3f4f6", "#9ca3af", "#374151"];
  ops.push((g, y) => {
    y += 14;
    g.fillStyle = tone[0] as string; g.fillRect(X, y, R - X, 100);
    g.strokeStyle = tone[1] as string; g.lineWidth = 3; g.strokeRect(X, y, R - X, 100);
    g.fillStyle = tone[2] as string; g.font = font(42, true); g.fillText(c.totalLabel, X + 30, y + 64);
    g.font = font(48, true);
    g.fillText(c.total, R - 30 - g.measureText(c.total).width, y + 67);
    return y + 136;
  });
  for (const n of c.notes) {
    const rows = wrap(n, 24, false, R - X);
    ops.push((g, y) => {
      g.font = font(24); g.fillStyle = "#555";
      rows.forEach((t, i) => g.fillText(t, X, y + 24 + i * 34));
      return y + rows.length * 34 + 8;
    });
  }

  // Measure on a throwaway pass, then draw at the right height.
  const dry = document.createElement("canvas"); dry.width = W; dry.height = 10;
  const dg = dry.getContext("2d") as CanvasRenderingContext2D;
  let h = 70; for (const op of ops) h = op(dg, h);

  const cv = document.createElement("canvas");
  cv.width = W * scale; cv.height = (h + 50) * scale;
  const g = cv.getContext("2d") as CanvasRenderingContext2D;
  g.fillStyle = "#fff"; g.fillRect(0, 0, cv.width, cv.height);
  let y = 70; for (const op of ops) y = op(g, y);

  return await new Promise<Blob>((ok, no) =>
    cv.toBlob((b) => (b ? ok(b) : no(new Error("Could not make the image."))), "image/png"));
}

/** Share sheet on a phone, a download everywhere else. */
export async function saveCard(c: Card, fileName: string): Promise<void> {
  const blob = await cardPng(c);
  const file = new File([blob], fileName, { type: "image/png" });
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    try { await nav.share({ files: [file], title: c.title }); return; } catch { /* fall through */ }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
